import React, { useMemo } from "react";
import PropTypes from "prop-types";
import TableStyle from "styles/application/TableStyle";
import PopupMenu from "components/Elements/PopupMenu";
import { ReactComponent as BlueMenuIcon } from "assets/icons/3dot-icon.svg";
import { ReactComponent as WhiteMenuIcon } from "assets/icons/3dot-white-icon.svg";
import { EDIT_ACCESS, FULL_ACCESS } from "constants/AccessTypes/AccessTypes";

const AT = TableStyle();

const buildMenuData = ({
	toggle,
	disableLink,
	customCaptions,
	onEdit,
	onDuplicate,
	onCopy,
	onCopyTaskQuestion,
	onSwitchToServiceLayout,
	onRemove,
}) => [
	{ name: "Edit", handler: () => onEdit(!toggle), isDelete: false },
	{ name: "Duplicate", handler: onDuplicate, isDelete: false },
	{ name: "Copy", handler: onCopy, isDelete: false },
	{
		name: `Copy ${customCaptions?.task} ${customCaptions?.questionPlural}`,
		handler: onCopyTaskQuestion,
		isDelete: false,
	},
	{
		name: `Switch To ${customCaptions?.service} Layout`,
		handler: onSwitchToServiceLayout,
		isDelete: false,
		disabled: disableLink,
	},
	{ name: "Remove", handler: onRemove, isDelete: true },
];

// Publish-state and access both narrow the menu further, independent of
// which items exist - kept separate from buildMenuData so each stays simple.
const filterMenuData = (menuData, { isPublished, access, customCaptions }) =>
	menuData.filter((item) => {
		if (isPublished) {
			return (
				item?.name.includes("Copy") ||
				item?.name === `Switch To ${customCaptions?.service} Layout`
			);
		}
		if (access === FULL_ACCESS) return true;
		if (access === EDIT_ACCESS) return item.name === "Edit";
		return false;
	});

const ModelTaskRowActions = ({
	access,
	toggle,
	index,
	selectedData,
	anchorEl,
	isLast,
	taskId,
	isPublished,
	disableLink,
	customCaptions,
	onDotMenuClick,
	onClickAway,
	onEdit,
	onDuplicate,
	onCopy,
	onCopyTaskQuestion,
	onSwitchToServiceLayout,
	onRemove,
}) => {
	const menuData = useMemo(
		() =>
			filterMenuData(
				buildMenuData({
					toggle,
					disableLink,
					customCaptions,
					onEdit,
					onDuplicate,
					onCopy,
					onCopyTaskQuestion,
					onSwitchToServiceLayout,
					onRemove,
				}),
				{ isPublished, access, customCaptions }
			),
		[
			access,
			customCaptions,
			disableLink,
			isPublished,
			onCopy,
			onCopyTaskQuestion,
			onDuplicate,
			onEdit,
			onRemove,
			onSwitchToServiceLayout,
			toggle,
		]
	);

	return (
		<AT.DotMenu onClick={onDotMenuClick} className="taskdotmenu">
			{(access === FULL_ACCESS || access === EDIT_ACCESS) && (
				<AT.TableMenuButton className="taskdotmenu">
					{toggle ? (
						<WhiteMenuIcon className="taskdotmenu" />
					) : (
						<BlueMenuIcon className="taskdotmenu" />
					)}
				</AT.TableMenuButton>
			)}
			<PopupMenu
				index={index}
				selectedData={selectedData}
				anchorEl={anchorEl}
				isLast={isLast}
				id={taskId}
				clickAwayHandler={onClickAway}
				menuData={menuData}
			/>
		</AT.DotMenu>
	);
};

ModelTaskRowActions.propTypes = {
	access: PropTypes.string,
	toggle: PropTypes.bool.isRequired,
	index: PropTypes.number.isRequired,
	selectedData: PropTypes.number,
	anchorEl: PropTypes.object,
	isLast: PropTypes.bool.isRequired,
	taskId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	isPublished: PropTypes.bool,
	disableLink: PropTypes.bool,
	customCaptions: PropTypes.object,
	onDotMenuClick: PropTypes.func.isRequired,
	onClickAway: PropTypes.func.isRequired,
	onEdit: PropTypes.func.isRequired,
	onDuplicate: PropTypes.func.isRequired,
	onCopy: PropTypes.func.isRequired,
	onCopyTaskQuestion: PropTypes.func.isRequired,
	onSwitchToServiceLayout: PropTypes.func.isRequired,
	onRemove: PropTypes.func.isRequired,
};

export default ModelTaskRowActions;
